'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetPublicPartnerCreatorRow } from '@morfi/backend-sdk-v2/client';
import { useAuth, useGetFollowsCounts } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { usePortfolioContext } from '@/components/portfolio/portfolio-context';
import { FollowButton } from '@/components/profile/follow-button';
import { FollowersDialog } from '@/components/profile/followers-dialog';
import { UserIdentity } from '@/components/profile/user-identity';
import { ProfileStreakStat } from '@/components/streaks/profile-streak-stat';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Agreement01IconSolid, GiftIconSolid, LinkForwardIconSolid, Settings02IconSolid, UserGroup02IconSolid, UserLove01IconSolid } from '@/components/ui/icon/glyphs';
import { IdentityPill } from '@/components/ui/identity-pill';
import { Panel } from '@/components/ui/panel';
import { onEvent } from '@/events';
import { Link } from '@/i18n/navigation';
import { checkGate } from '@/lib/analytics';
import { useImpersonationStore } from '@/lib/impersonation/store';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { ShareModal } from '@/lib/share';
import { cn } from '@/lib/ui/utils';
import { PartnerProfileDetails } from '../partner/partner-profile-details';
import { ProfileActivityStat, ProfileActivityStats } from './profile-activity-stats';
import { ProfilePortfolioImages } from './profile-portfolio-images';
interface ConnectedProvider {
  provider: string;
  username?: string;
  displayName?: string;
}
export interface ProfilePortfolioCardProps {
  creatorUserId: string | null;
  partnerProfile?: GetPublicPartnerCreatorRow;
  /** Wallets whose trades drive the streak stat. */
  walletPubkeys?: readonly string[];
  /** Display name to render. Sourced from the public-user data path. */
  displayName: string | null;
  /** @-handle. Sourced from the public-user data path. */
  handle: string | null;
  bio: string | null;
  /** Photo URL — empty/null falls through to deterministic-palette initials. */
  avatarUrl: string | null;
  /** Spec-071 NSFW flag for the avatar. */
  avatarNsfw?: boolean | null;
  /** `users.avatar_preferences.rasterise` — drives the dither-vs-raw render. */
  avatarRasterise?: boolean | null;
  /** Hero / cover URL — empty/null falls through to user-seeded gradient. */
  heroUrl: string | null;
  /** Spec-071 NSFW flag for the hero. */
  coverNsfw?: boolean | null;
  /** Provider badge list (X / Google / Apple). */
  connectedProviders: ConnectedProvider[];
  /** Outer className passthrough — typically `min-w-0` from the parent grid. */
  className?: string;
}
function buildProfileShareUrl(userId: string, network: 'mainnet' | 'devnet'): string {
  if (typeof window === 'undefined') return '';
  const u = new URL(`/portfolio/${userId}`, window.location.origin);
  withDemoMode(u, network);
  return u.toString();
}

/**
 * `<ProfilePortfolioCard>` — compact left-column card (~40% width beside the PnL
 * hero at md+) at the top of `/portfolio` and `/portfolio/[user_id]`, paired
 * with the sibling PnL card (~60%). Surfaces hero, 96 px avatar, identity,
 * share button, and an inline Followers / Following row.
 *
 * Owner viewers see inline-edit affordances on hero and avatar; impersonating
 * engineers see the read-only branch (writes are also blocked at the SDK
 * boundary by `useGuardedSdkMutation`).
 *
 * Spec: 081-profile-portfolio-card
 */
export function ProfilePortfolioCard(props) {
  const $ = _c(136);
  const {
    creatorUserId,
    partnerProfile,
    walletPubkeys,
    displayName,
    handle,
    bio,
    avatarUrl,
    avatarNsfw,
    avatarRasterise,
    heroUrl,
    coverNsfw,
    connectedProviders,
    className
  } = props;
  const t = useTranslations("profile.next.portfolio_card");
  const tProfile = useTranslations("profile");
  const tPartner = useTranslations("profile.partner");
  const {
    viewerMode,
    userId
  } = usePortfolioContext();
  const isOwn = viewerMode === "own";
  const {
    user,
    setUser
  } = useAuth();
  const {
    network
  } = useUserNetwork();
  const isImpersonating = useImpersonationStore(_temp);
  const [followersOpen, setFollowersOpen] = useState(false);
  const [followersTab, setFollowersTab] = useState("followers");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["followers", "following"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== userId) {
    t1 = {
      userId,
      kind: "counts",
      fields: t0
    };
    $[1] = userId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const followCounts = useGetFollowsCounts(t1);
  const followersCount = followCounts.isPending ? null : followCounts.data?.followers ?? 0;
  const followingCount = followCounts.isPending ? null : followCounts.data?.following ?? 0;
  const t2 = network === "mainnet" ? "mainnet" : "devnet";
  let t3;
  if ($[3] !== t2 || $[4] !== userId) {
    t3 = buildProfileShareUrl(userId, t2);
    $[3] = t2;
    $[4] = userId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const shareUrl = t3;
  let t4;
  if ($[6] !== displayName || $[7] !== handle || $[8] !== tProfile) {
    t4 = handle !== null && handle.length > 0 ? tProfile("share_text", {
      recipientHandle: `@${handle}`
    }) : displayName !== null && displayName.length > 0 ? tProfile("share_text", {
      recipientHandle: displayName
    }) : tProfile("share_text_anonymous");
    $[6] = displayName;
    $[7] = handle;
    $[8] = tProfile;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const shareText = t4;
  const showOwnerEdit = isOwn && user !== null && !isImpersonating && partnerProfile === undefined;
  let t5;
  if ($[10] !== isOwn) {
    t5 = isOwn && checkGate("rewards_platform");
    $[10] = isOwn;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const showRewards = t5;
  let t6;
  if ($[12] !== setUser || $[13] !== user) {
    t6 = function handleAvatarChange(next, t7) {
      const avatarNsfw_0 = t7 === undefined ? false : t7;
      if (user === null) {
        return;
      }
      setUser({
        ...user,
        avatarUrl: next.length > 0 ? next : null,
        avatarNsfw: avatarNsfw_0
      });
    };
    $[12] = setUser;
    $[13] = user;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleAvatarChange = t6;
  let t7;
  if ($[15] !== setUser || $[16] !== user) {
    t7 = function handleHeroChange(next_0) {
      if (user === null) {
        return;
      }
      setUser({
        ...user,
        heroUrl: next_0.length > 0 ? next_0 : null
      });
    };
    $[15] = setUser;
    $[16] = user;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleHeroChange = t7;
  let t8;
  if ($[18] !== userId || $[19] !== viewerMode) {
    t8 = function handleShareClick() {
      onEvent("profile_portfolio_card_share_clicked", {
        viewerMode,
        targetUserId: userId
      });
    };
    $[18] = userId;
    $[19] = viewerMode;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleShareClick = t8;
  let t9;
  if ($[21] !== userId || $[22] !== viewerMode) {
    t9 = function handleFollowersClick() {
      setFollowersTab("followers");
      setFollowersOpen(true);
      onEvent("profile_portfolio_card_kpi_clicked", {
        viewerMode,
        targetUserId: userId,
        kpi: "followers"
      });
    };
    $[21] = userId;
    $[22] = viewerMode;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleFollowersClick = t9;
  let t10;
  if ($[24] !== userId || $[25] !== viewerMode) {
    t10 = function handleFollowingClick() {
      setFollowersTab("following");
      setFollowersOpen(true);
      onEvent("profile_portfolio_card_kpi_clicked", {
        viewerMode,
        targetUserId: userId,
        kpi: "following"
      });
    };
    $[24] = userId;
    $[25] = viewerMode;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const handleFollowingClick = t10;
  let t11;
  if ($[27] !== className) {
    t11 = cn("relative gap-0 pt-0", className);
    $[27] = className;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = user?.avatarPreferences?.rasterise ?? false;
  let t13;
  if ($[29] !== avatarNsfw || $[30] !== avatarRasterise || $[31] !== avatarUrl || $[32] !== coverNsfw || $[33] !== displayName || $[34] !== handleAvatarChange || $[35] !== handleHeroChange || $[36] !== heroUrl || $[37] !== partnerProfile || $[38] !== showOwnerEdit || $[39] !== t12 || $[40] !== userId) {
    t13 = <ProfilePortfolioImages partnerProfile={partnerProfile} userId={userId} displayName={displayName} avatarUrl={avatarUrl} heroUrl={heroUrl} avatarNsfw={avatarNsfw} coverNsfw={coverNsfw} avatarRasterise={avatarRasterise} ownerRasterise={t12} showOwnerEdit={showOwnerEdit} handleHeroChange={handleHeroChange} handleAvatarChange={handleAvatarChange} />;
    $[29] = avatarNsfw;
    $[30] = avatarRasterise;
    $[31] = avatarUrl;
    $[32] = coverNsfw;
    $[33] = displayName;
    $[34] = handleAvatarChange;
    $[35] = handleHeroChange;
    $[36] = heroUrl;
    $[37] = partnerProfile;
    $[38] = showOwnerEdit;
    $[39] = t12;
    $[40] = userId;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== userId || $[43] !== viewerMode) {
    t14 = viewerMode === "other" ? <FollowButton targetUserId={userId} /> : null;
    $[42] = userId;
    $[43] = viewerMode;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== showRewards || $[46] !== t) {
    t15 = showRewards ? <ProfileRewardsButton ariaLabel={t("rewards_label")} /> : null;
    $[45] = showRewards;
    $[46] = t;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== isOwn || $[49] !== t) {
    t16 = isOwn ? <ProfileSettingsButton ariaLabel={t("settings_label")} /> : null;
    $[48] = isOwn;
    $[49] = t;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== t) {
    t17 = t("share_title");
    $[51] = t;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== t) {
    t18 = t("share_label");
    $[53] = t;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== handleShareClick || $[56] !== shareText || $[57] !== shareUrl || $[58] !== t17 || $[59] !== t18 || $[60] !== userId) {
    t19 = <ProfileShareTrigger userId={userId} url={shareUrl} text={shareText} title={t17} ariaLabel={t18} onClick={handleShareClick} />;
    $[55] = handleShareClick;
    $[56] = shareText;
    $[57] = shareUrl;
    $[58] = t17;
    $[59] = t18;
    $[60] = userId;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== t14 || $[63] !== t15 || $[64] !== t16 || $[65] !== t19) {
    t20 = <div className="flex items-center justify-end gap-2 pl-4 pt-2 pr-3 sm:pl-6 sm:pr-3">{t14}{t15}{t16}{t19}</div>;
    $[62] = t14;
    $[63] = t15;
    $[64] = t16;
    $[65] = t19;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  const t21 = `/portfolio/${userId}`;
  const t22 = displayName ?? "";
  let t23;
  if ($[67] !== t || $[68] !== t22) {
    t23 = t("open_profile_aria", {
      displayName: t22
    });
    $[67] = t;
    $[68] = t22;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== avatarNsfw || $[71] !== avatarUrl || $[72] !== connectedProviders || $[73] !== displayName || $[74] !== handle || $[75] !== partnerProfile || $[76] !== tPartner || $[77] !== userId) {
    t24 = partnerProfile ? <><div className="flex flex-wrap items-center gap-2.5"><h1 className="min-w-0"><UserIdentity user={{
            userId,
            displayName: partnerProfile.displayName,
            handle: partnerProfile.handle,
            avatarUrl: partnerProfile.avatarUrl,
            avatarNsfw: avatarNsfw ?? null,
            connectedProviders: []
          }} displayNameOverride={partnerProfile.displayName} showHandle={false} nameClassName="text-heading-lg" /></h1><IdentityPill tone="partner" icon={Agreement01IconSolid}>{tPartner("badge")}</IdentityPill></div><p className="break-words text-caption text-foreground-tertiary">@{partnerProfile.handle}{partnerProfile.location ? ` · ${partnerProfile.location}` : ""}</p></> : <UserIdentity user={{
      userId,
      displayName,
      handle,
      avatarUrl,
      connectedProviders
    }} variant="stack" size="md" showProviderBadges={true} nameClassName="text-heading-md" handleClassName="text-caption" />;
    $[70] = avatarNsfw;
    $[71] = avatarUrl;
    $[72] = connectedProviders;
    $[73] = displayName;
    $[74] = handle;
    $[75] = partnerProfile;
    $[76] = tPartner;
    $[77] = userId;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  let t25;
  if ($[79] !== t21 || $[80] !== t23 || $[81] !== t24) {
    t25 = <Link href={t21} aria-label={t23} className="flex min-w-0 flex-col gap-0.5 px-4 pb-0 pt-5 sm:px-6">{t24}</Link>;
    $[79] = t21;
    $[80] = t23;
    $[81] = t24;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  let t26;
  if ($[83] !== bio || $[84] !== partnerProfile) {
    t26 = partnerProfile !== undefined ? <PartnerProfileDetails profile={partnerProfile} /> : bio?.trim() ? <p className="mt-4 whitespace-pre-wrap break-words px-4 text-body text-foreground-primary sm:px-6">{bio}</p> : null;
    $[83] = bio;
    $[84] = partnerProfile;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  const t27 = followersCount ?? 0;
  let t28;
  if ($[86] !== t || $[87] !== t27) {
    t28 = t("followers_aria", {
      count: t27
    });
    $[86] = t;
    $[87] = t27;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  let t29;
  if ($[89] !== t) {
    t29 = t("followers_label");
    $[89] = t;
    $[90] = t29;
  } else {
    t29 = $[90];
  }
  let t30;
  if ($[91] !== followersCount || $[92] !== t29) {
    t30 = <ProfileActivityStat icon={UserGroup02IconSolid} value={followersCount} label={t29} />;
    $[91] = followersCount;
    $[92] = t29;
    $[93] = t30;
  } else {
    t30 = $[93];
  }
  let t31;
  if ($[94] !== handleFollowersClick || $[95] !== t28 || $[96] !== t30) {
    t31 = <button type="button" onClick={handleFollowersClick} aria-label={t28} className="inline-flex rounded-item transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary">{t30}</button>;
    $[94] = handleFollowersClick;
    $[95] = t28;
    $[96] = t30;
    $[97] = t31;
  } else {
    t31 = $[97];
  }
  const t32 = followingCount ?? 0;
  let t33;
  if ($[98] !== t || $[99] !== t32) {
    t33 = t("following_aria", {
      count: t32
    });
    $[98] = t;
    $[99] = t32;
    $[100] = t33;
  } else {
    t33 = $[100];
  }
  let t34;
  if ($[101] !== t) {
    t34 = t("following_label");
    $[101] = t;
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  let t35;
  if ($[103] !== followingCount || $[104] !== t34) {
    t35 = <ProfileActivityStat icon={UserLove01IconSolid} value={followingCount} label={t34} />;
    $[103] = followingCount;
    $[104] = t34;
    $[105] = t35;
  } else {
    t35 = $[105];
  }
  let t36;
  if ($[106] !== handleFollowingClick || $[107] !== t33 || $[108] !== t35) {
    t36 = <button type="button" onClick={handleFollowingClick} aria-label={t33} className="inline-flex rounded-item transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary">{t35}</button>;
    $[106] = handleFollowingClick;
    $[107] = t33;
    $[108] = t35;
    $[109] = t36;
  } else {
    t36 = $[109];
  }
  let t37;
  if ($[110] !== partnerProfile || $[111] !== userId || $[112] !== walletPubkeys) {
    t37 = partnerProfile === undefined ? <ProfileStreakStat userId={userId} walletPubkeys={walletPubkeys} showZero={true} /> : null;
    $[110] = partnerProfile;
    $[111] = userId;
    $[112] = walletPubkeys;
    $[113] = t37;
  } else {
    t37 = $[113];
  }
  let t38;
  if ($[114] !== creatorUserId || $[115] !== walletPubkeys) {
    t38 = <ProfileActivityStats creatorUserId={creatorUserId} walletPubkeys={walletPubkeys} />;
    $[114] = creatorUserId;
    $[115] = walletPubkeys;
    $[116] = t38;
  } else {
    t38 = $[116];
  }
  let t39;
  if ($[117] !== t31 || $[118] !== t36 || $[119] !== t37 || $[120] !== t38) {
    t39 = <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pb-0 sm:px-6">{t31}{t36}{t37}{t38}</div>;
    $[117] = t31;
    $[118] = t36;
    $[119] = t37;
    $[120] = t38;
    $[121] = t39;
  } else {
    t39 = $[121];
  }
  let t40;
  if ($[122] !== t11 || $[123] !== t13 || $[124] !== t20 || $[125] !== t25 || $[126] !== t26 || $[127] !== t39) {
    t40 = <Panel className={t11}>{t13}{t20}{t25}{t26}{t39}</Panel>;
    $[122] = t11;
    $[123] = t13;
    $[124] = t20;
    $[125] = t25;
    $[126] = t26;
    $[127] = t39;
    $[128] = t40;
  } else {
    t40 = $[128];
  }
  let t41;
  if ($[129] !== followersOpen || $[130] !== followersTab || $[131] !== userId) {
    t41 = <FollowersDialog open={followersOpen} onOpenChange={setFollowersOpen} userId={userId} defaultTab={followersTab} />;
    $[129] = followersOpen;
    $[130] = followersTab;
    $[131] = userId;
    $[132] = t41;
  } else {
    t41 = $[132];
  }
  let t42;
  if ($[133] !== t40 || $[134] !== t41) {
    t42 = <>{t40}{t41}</>;
    $[133] = t40;
    $[134] = t41;
    $[135] = t42;
  } else {
    t42 = $[135];
  }
  return t42;
}

// ---------------------------------------------------------------------------
// Internal — share trigger split into a child for readability.
// ---------------------------------------------------------------------------
function _temp(s) {
  return s.isActive;
}
interface ProfileShareTriggerProps {
  userId: string;
  url: string;
  text: string;
  title: string;
  ariaLabel: string;
  onClick: () => void;
}
function ProfileShareTrigger(props) {
  const $ = _c(12);
  let t0;
  if ($[0] !== props.userId) {
    t0 = {
      kind: "profile",
      userId: props.userId
    };
    $[0] = props.userId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={LinkForwardIconSolid} className="size-4" aria-hidden="true" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props.ariaLabel || $[4] !== props.onClick) {
    t2 = <AnimatedButton variant="secondary" size="default" aria-label={props.ariaLabel} onClick={props.onClick}>{t1}</AnimatedButton>;
    $[3] = props.ariaLabel;
    $[4] = props.onClick;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props.text || $[7] !== props.title || $[8] !== props.url || $[9] !== t0 || $[10] !== t2) {
    t3 = <ShareModal surface={t0} url={props.url} text={props.text} title={props.title} trigger={t2} />;
    $[6] = props.text;
    $[7] = props.title;
    $[8] = props.url;
    $[9] = t0;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}

/** Gear button — own-profile only, mirrors the share trigger and links to settings.
 *  `w-9 px-0` reproduces the share button's icon-only square (`size-9`); the
 *  auto square-detection only fires on a direct `<Icon>` child, not through the
 *  `asChild` <Link>. */
function ProfileSettingsButton(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Link href="/settings"><Icon icon={Settings02IconSolid} className="size-4" aria-hidden="true" /></Link>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.ariaLabel) {
    t1 = <AnimatedButton variant="secondary" size="default" aria-label={props.ariaLabel} asChild={true} className="w-9 px-0">{t0}</AnimatedButton>;
    $[1] = props.ariaLabel;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/** Rewards shortcut — own-profile only and hidden with the rewards platform gate. */
function ProfileRewardsButton(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Link href="/rewards" onClick={_temp2}><Icon icon={GiftIconSolid} className="size-4" aria-hidden="true" /></Link>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.ariaLabel) {
    t1 = <AnimatedButton variant="secondary" size="default" aria-label={props.ariaLabel} asChild={true} className="w-9 px-0">{t0}</AnimatedButton>;
    $[1] = props.ariaLabel;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function _temp2() {
  onEvent("rewards_entry_clicked", {
    authenticated: true,
    source: "portfolio"
  });
}